import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Outlet } from 'react-router-dom';
import PainManagement from './pages/PainManagement';
import BrainTMS from './pages/BrainTMS';
import TraumaticBrainInjury from './pages/TraumaticBrainInjury';
import WorkAndPersonalInjury from './pages/WorkAndPersonalInjury';
import AdditionalTreatments from './pages/AdditionalTreatments';
import Contact from './pages/Contact';
import PreOperativeInstructions from './pages/PreOperativeInstructions';
import PatientRightsAndPolicies from './pages/PatientRightsAndPolicies';
import Blog from './pages/Blog';
import JobOpenings from './pages/JobOpenings';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import AllTreatments from './pages/AllTreatments';
import Neuroanatomy from './pages/Neuroanatomy';
import InnovativeIntegrativeMedicine from './pages/InnovativeIntegrativeMedicine';
import LongTermEffectsTBI from './pages/LongTermEffectsTBI';

const AppLayout = ({
  t,
  language,
  handleLanguageChange,
  showLanguageDropdown,
  setShowLanguageDropdown,
  accessibilitySettings,
  toggleAccessibilityPanel,
  handleAccessibilityChange,
  mobileMenuOpen,
  setMobileMenuOpen,
  servicesOpen,
  setServicesOpen,
  patientPortalOpen,
  setPatientPortalOpen,
  educationOpen,
  setEducationOpen
}) => (
  <div className="min-h-screen bg-white">
    {/* Navigation */}
    <nav className="bg-white border-b border-gray-100/50 sticky top-0 z-50">
      <div className="w-full px-0 sm:px-2 lg:px-4">
        {/* Top bar for language and accessibility */}
        <div className="flex justify-end items-center h-6 pt-1">
          <div className="flex items-center space-x-2">
            {/* Language Switcher */}
            <div className="relative language-dropdown">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className="flex items-center space-x-1 text-gray-600 hover:text-teal-600 px-2 py-1 rounded text-xs font-medium transition-all duration-300 hover:bg-teal-50"
              >
                <span>{language === 'en' ? '🇺🇸 EN' : language === 'es' ? '🇪🇸 ES' : '🇮🇳 HI'}</span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showLanguageDropdown && (
                <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-teal-50 transition-colors rounded-t-lg ${
                      language === 'en' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'
                    }`}
                  >
                    🇺🇸 English
                  </button>
                  <button
                    onClick={() => handleLanguageChange('es')}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-teal-50 transition-colors ${
                      language === 'es' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'
                    }`}
                  >
                    🇪🇸 Español
                  </button>
                  <button
                    onClick={() => handleLanguageChange('hi')}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-teal-50 transition-colors rounded-b-lg ${
                      language === 'hi' ? 'text-teal-600 font-semibold bg-teal-50' : 'text-gray-700'
                    }`}
                  >
                    🇮🇳 हिंदी
                  </button>
                </div>
              )}
            </div>

            {/* Accessibility Button */}
            <button
              onClick={toggleAccessibilityPanel}
              className="flex items-center space-x-1 text-gray-600 hover:text-teal-600 px-2 py-1 rounded text-xs font-medium transition-all duration-300 hover:bg-teal-50"
              aria-label={t.accessibility.title}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="hidden sm:inline">{t.accessibility.title}</span>
            </button>
          </div>
        </div>
        
        {/* Main navigation */}
        <div className="flex justify-between h-20">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                {/* Original Logo Image */}
                <div className="flex items-center justify-center">
                  <img 
                    src="/logo.png" 
                    alt="Mind and Body Pain Clinic Logo" 
                    className="h-12 w-auto object-contain object-left"
                    style={{ marginRight: '-10px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  {/* Fallback if image doesn't load */}
                  <div className="h-12 w-12 bg-teal-100 rounded-lg flex items-center justify-center hidden">
                    <span className="text-teal-600 font-bold text-sm">Logo</span>
                  </div>
                </div>
                {/* Clinic Name */}
                <div className="hidden sm:block ml-4">
                  <div className="text-xl font-bold text-gray-900 leading-tight">
                    <div className="tracking-wide">Mind and Body</div>
                    <div className="text-teal-600 font-semibold">Pain Clinic</div>
                  </div>
                </div>
                <div className="sm:hidden">
                  <div className="text-lg font-bold text-gray-900 tracking-wide">MBPC</div>
                </div>
              </Link>
            </div>
          </div>
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Services Dropdown */}
            <div className="relative services-dropdown">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center text-gray-700 hover:text-teal-600 px-2 py-2 text-sm font-medium transition-all duration-300 hover:bg-teal-50/80 whitespace-nowrap"
              >
                {t.nav.services}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {servicesOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <Link to="/traumatic-brain-injury" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors rounded-t-lg">
                    {t.nav.traumaticBrainInjury}
                  </Link>
                  <Link to="/pain-management" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors">
                    {t.nav.silverCreekSurgery}
                  </Link>
                  <Link to="/brain-tms" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors">
                    {t.nav.tms}
                  </Link>
                  <Link to="/all-treatments" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors rounded-b-lg">
                    All Treatments
                    </Link>
                  </div>
              )}
            </div>
          {/* Patient Portal Dropdown */}
          <div className="relative patient-portal-dropdown">
            <button
              onClick={() => setPatientPortalOpen(!patientPortalOpen)}
              className="flex items-center text-gray-700 hover:text-teal-600 px-2 py-2 text-sm font-medium transition-all duration-300 hover:bg-teal-50/80 whitespace-nowrap"
            >
              {t.nav.patientPortal}
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {patientPortalOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <Link to="/pre-operative-instructions" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors rounded-t-lg">
                  {t.nav.preOperativeInstructions}
                </Link>
                <Link to="/patient-rights-and-policies" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors rounded-b-lg">
                  {t.nav.patientRightsAndPolicies}
                </Link>
                </div>
              )}
            </div>

            {/* Education Dropdown */}
            <div className="relative education-dropdown">
              <button
                onClick={() => setEducationOpen(!educationOpen)}
                className="flex items-center text-gray-700 hover:text-teal-600 px-2 py-2 text-sm font-medium transition-all duration-300 hover:bg-teal-50/80 whitespace-nowrap"
              >
                {t.nav.education}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {educationOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <Link to="/neuroanatomy" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors rounded-t-lg">
                    {t.nav.neuroanatomy}
                  </Link>
                  <Link to="/innovative-integrative-medicine" className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 transition-colors rounded-b-lg">
                    {t.nav.innovativeIntegrativeMedicine}
                  </Link>
                </div>
              )}
            </div>

            <Link to="/work-and-personal-injury" className="text-gray-700 hover:text-teal-600 px-2 py-2 text-sm font-medium transition-all duration-300 hover:bg-teal-50/80 whitespace-nowrap">{t.nav.workAndPersonalInjury}</Link>
            <Link to="/blog" className="text-gray-700 hover:text-teal-600 px-2 py-2 text-sm font-medium transition-all duration-300 hover:bg-teal-50/80 whitespace-nowrap">{t.nav.blog}</Link>
            <Link to="/contact" className="text-gray-700 hover:text-teal-600 px-2 py-2 text-sm font-medium transition-all duration-300 hover:bg-teal-50/80 whitespace-nowrap">{t.nav.contact}</Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-teal-600 p-2 rounded-lg transition-all duration-300 hover:bg-teal-50 mobile-menu"
              aria-label="Toggle mobile menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>
      </div>
      </nav>

    {/* Mobile Menu Dropdown */}
    {mobileMenuOpen && (
      <div className="lg:hidden bg-white border-t border-gray-200/50 shadow-xl mobile-menu">
        <div className="px-6 py-8 space-y-4">
          {/* Mobile Services Dropdown */}
          <div>
            <button
              onClick={() => setServicesOpen(!servicesOpen)}
              className="w-full flex justify-between items-center text-gray-700 hover:text-teal-600 px-6 py-4 text-lg font-medium transition-all duration-300 hover:bg-teal-50/80"
            >
              <span>{t.nav.services}</span>
              <svg className={`w-5 h-5 transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {servicesOpen && (
              <div className="pl-8 pt-2 pb-2 space-y-2">
                <Link to="/traumatic-brain-injury" className="block text-gray-600 hover:text-teal-600 px-4 py-2 text-base font-medium transition-all duration-300 hover:bg-teal-50/80" onClick={() => setMobileMenuOpen(false)}>
                  {t.nav.traumaticBrainInjury}
                </Link>
                <Link to="/pain-management" className="block text-gray-600 hover:text-teal-600 px-4 py-2 text-base font-medium transition-all duration-300 hover:bg-teal-50/80" onClick={() => setMobileMenuOpen(false)}>
                  {t.nav.silverCreekSurgery}
                </Link>
                <Link to="/brain-tms" className="block text-gray-600 hover:text-teal-600 px-4 py-2 text-base font-medium transition-all duration-300 hover:bg-teal-50/80" onClick={() => setMobileMenuOpen(false)}>
                  {t.nav.tms}
                </Link>
              </div>
            )}
          </div>
          {/* Mobile Patient Portal Dropdown */}
          <div>
            <button
              onClick={() => setPatientPortalOpen(!patientPortalOpen)}
              className="w-full flex justify-between items-center text-gray-700 hover:text-teal-600 px-6 py-4 text-lg font-medium transition-all duration-300 hover:bg-teal-50/80"
            >
              <span>{t.nav.patientPortal}</span>
              <svg className={`w-5 h-5 transition-transform duration-300 ${patientPortalOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {patientPortalOpen && (
              <div className="pl-8 pt-2 pb-2 space-y-2">
                <Link to="/pre-operative-instructions" className="block text-gray-600 hover:text-teal-600 px-4 py-2 text-base font-medium transition-all duration-300 hover:bg-teal-50/80" onClick={() => setMobileMenuOpen(false)}>
                  {t.nav.preOperativeInstructions}
                </Link>
                <Link to="/patient-rights-and-policies" className="block text-gray-600 hover:text-teal-600 px-4 py-2 text-base font-medium transition-all duration-300 hover:bg-teal-50/80" onClick={() => setMobileMenuOpen(false)}>
                  {t.nav.patientRightsAndPolicies}
                </Link>
              </div>
            )}
          </div>
          {/* Mobile Education Dropdown */}
          <div>
            <button
              onClick={() => setEducationOpen(!educationOpen)}
              className="w-full flex justify-between items-center text-gray-700 hover:text-teal-600 px-6 py-4 text-lg font-medium transition-all duration-300 hover:bg-teal-50/80"
            >
              <span>{t.nav.education}</span>
              <svg className={`w-5 h-5 transition-transform duration-300 ${educationOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {educationOpen && (
              <div className="pl-8 pt-2 pb-2 space-y-2">
                <Link to="/neuroanatomy" className="block text-gray-600 hover:text-teal-600 px-4 py-2 text-base font-medium transition-all duration-300 hover:bg-teal-50/80" onClick={() => setMobileMenuOpen(false)}>
                  {t.nav.neuroanatomy}
                </Link>
                <Link to="/innovative-integrative-medicine" className="block text-gray-600 hover:text-teal-600 px-4 py-2 text-base font-medium transition-all duration-300 hover:bg-teal-50/80" onClick={() => setMobileMenuOpen(false)}>
                  {t.nav.innovativeIntegrativeMedicine}
                </Link>
              </div>
            )}
          </div>
                <Link
            to="/work-and-personal-injury"
            className="block text-gray-700 hover:text-teal-600 px-6 py-4 text-lg font-medium transition-all duration-300 hover:bg-teal-50/80"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t.nav.workAndPersonalInjury}
          </Link>
                <Link
            to="/blog"
            className="block text-gray-700 hover:text-teal-600 px-6 py-4 text-lg font-medium transition-all duration-300 hover:bg-teal-50/80"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t.nav.blog}
          </Link>
                <Link
            to="/contact" 
            className="block text-gray-700 hover:text-teal-600 px-6 py-4 text-lg font-medium transition-all duration-300 hover:bg-teal-50/80"
            onClick={() => setMobileMenuOpen(false)}
          >
            {t.nav.contact}
          </Link>
        </div>
      </div>
    )}

    {/* Accessibility Panel */}
    {accessibilitySettings.showAccessibilityPanel && (
      <div className="fixed top-20 right-4 w-80 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-gray-200/50 z-50 accessibility-panel">
        <div className="p-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t.accessibility.title}</h3>
            <button
              onClick={toggleAccessibilityPanel}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label={t.accessibility.close}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Text Size Controls */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Text Size</label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleAccessibilityChange('fontSize', Math.max(80, accessibilitySettings.fontSize - 10))}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                  aria-label={t.accessibility.decreaseText}
                >
                  A-
                </button>
                <span className="text-sm text-gray-600 min-w-[3rem] text-center">
                  {accessibilitySettings.fontSize}%
                </span>
                <button
                  onClick={() => handleAccessibilityChange('fontSize', Math.min(200, accessibilitySettings.fontSize + 10))}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                  aria-label={t.accessibility.increaseText}
                >
                  A+
                </button>
              </div>
            </div>

            {/* High Contrast Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">{t.accessibility.highContrast}</label>
              <button
                onClick={() => handleAccessibilityChange('highContrast', !accessibilitySettings.highContrast)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  accessibilitySettings.highContrast ? 'bg-teal-600' : 'bg-gray-200'
                }`}
                aria-label={`${t.accessibility.highContrast} ${accessibilitySettings.highContrast ? 'on' : 'off'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    accessibilitySettings.highContrast ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Reduce Motion Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">{t.accessibility.reduceMotion}</label>
              <button
                onClick={() => handleAccessibilityChange('reduceMotion', !accessibilitySettings.reduceMotion)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  accessibilitySettings.reduceMotion ? 'bg-teal-600' : 'bg-gray-200'
                }`}
                aria-label={`${t.accessibility.reduceMotion} ${accessibilitySettings.reduceMotion ? 'on' : 'off'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    accessibilitySettings.reduceMotion ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Reset Button */}
            <button
              onClick={() => {
                handleAccessibilityChange('fontSize', 100);
                handleAccessibilityChange('highContrast', false);
                handleAccessibilityChange('reduceMotion', false);
              }}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              {t.accessibility.reset}
            </button>
          </div>
        </div>
      </div>
    )}
    
    <main>
      <Outlet />
    </main>

    <Footer t={t} />
  </div>
);

function App() {
  const [language, setLanguage] = useState('en');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [accessibilitySettings, setAccessibilitySettings] = useState({
    fontSize: 100, // percentage
    highContrast: false,
    reduceMotion: false,
    showAccessibilityPanel: false
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [patientPortalOpen, setPatientPortalOpen] = useState(false);
  const [educationOpen, setEducationOpen] = useState(false);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setShowLanguageDropdown(false);
  };

  const handleAccessibilityChange = (setting, value) => {
    setAccessibilitySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const toggleAccessibilityPanel = () => {
    setAccessibilitySettings(prev => ({
      ...prev,
      showAccessibilityPanel: !prev.showAccessibilityPanel
    }));
  };

  // Close language dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLanguageDropdown && !event.target.closest('.language-dropdown')) {
        setShowLanguageDropdown(false);
      }
      if (accessibilitySettings.showAccessibilityPanel && !event.target.closest('.accessibility-panel')) {
        setAccessibilitySettings(prev => ({ ...prev, showAccessibilityPanel: false }));
      }
      if (mobileMenuOpen && !event.target.closest('.mobile-menu')) {
        setMobileMenuOpen(false);
      }
      if (servicesOpen && !event.target.closest('.services-dropdown')) {
        setServicesOpen(false);
      }
      if (patientPortalOpen && !event.target.closest('.patient-portal-dropdown')) {
        setPatientPortalOpen(false);
      }
      if (educationOpen && !event.target.closest('.education-dropdown')) {
        setEducationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageDropdown, accessibilitySettings.showAccessibilityPanel, mobileMenuOpen, servicesOpen, patientPortalOpen, educationOpen]);

  // Apply accessibility settings to document
  React.useEffect(() => {
    const root = document.documentElement;
    
    // Apply font size
    root.style.fontSize = `${accessibilitySettings.fontSize}%`;
    
    // Apply high contrast
    if (accessibilitySettings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Apply reduced motion
    if (accessibilitySettings.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
  }, [accessibilitySettings]);

  // Language content
  const content = {
    en: {
      nav: {
        home: 'Home',
        about: 'About Us',
        services: 'Services',
        traumaticBrainInjury: 'Traumatic Brain Injury',
        silverCreekSurgery: 'Pain Management',
        tms: 'TMS',
        contact: 'Contact Us',
        patientPortal: 'Patient Portal',
        preOperativeInstructions: 'Pre-Operative Instructions',
        patientRightsAndPolicies: 'Patient Rights and Policies',
        workAndPersonalInjury: 'Work & Personal Injury',
        blog: 'Blog',
        education: 'Education',
        neuroanatomy: 'Neuroanatomy and Neurodegeneration',
        innovativeIntegrativeMedicine: 'Innovative & Integrative Medicine'
      },
      hero: {
        welcome: 'Welcome to',
        subtitle: 'Comprehensive pain management and wellness solutions.',
        getStarted: 'Get Started',
        learnMore: 'Learn More'
      },
      about: {
        title: 'Our Mission',
        description: "At Mind and Body Pain Clinic, we are dedicated to the comprehensive evaluation and multimodal management of pain and neurological conditions. We specialize in treating headaches, neck pain, and a wide range of other painful disorders.\n\nOur mission is to support patients through education, medication management, and advanced interventional procedures. We believe in combining modern medical technologies with complementary approaches such as physical therapy and yoga to deliver truly holistic care.\n\nWe work closely with your healthcare providers to ensure individualized treatment tailored to your needs. While we use medications when appropriate, we typically avoid high-dose opioid regimens, as research shows they carry significant risks with limited long-term benefit compared to safer, lower-dose strategies."
      },
             services: {
         title: 'Our Services',
         traumaticBrainInjury: {
           title: 'Traumatic Brain Injury Services',
           description: 'Holistic treatment plans for neurological conditions.',
           learnMore: 'Learn more →',
           features: [
             'Personalized treatment plans',
             'Multidisciplinary approach',
             'Evidence-based therapies'
           ]
         },
         silverCreekSurgery: {
           title: 'Pain Management Services',
           description: 'State-of-the-art treatments for acute and chronic pain.',
           learnMore: 'Learn more →',
           features: [
             'Minimally invasive procedures',
             'Advanced surgical techniques',
             'Comprehensive post-operative care'
           ]
         },
         brainTMS: {
           title: 'Brain TMS Services',
           description: 'TMS therapy for neurological and psychiatric conditions.',
           learnMore: 'Learn more →',
           features: [
             'Non-invasive treatment',
             'No medication side effects',
             'FDA-approved therapy'
           ]
         }
       },
      contact: {
        title: 'Get In Touch',
        subtitle: 'Ready to start your journey to pain relief? Contact us today for a free consultation.',
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        message: 'Message',
        sendMessage: 'Send Message',
        sending: 'Sending...'
      },
             footer: {
         tagline: 'Comprehensive care for chronic pain and neurological conditions.',
         description: 'Professional pain management and wellness solutions.',
         contact: 'Contact',
         address: '6010 Hellyer Ave Ste 150, San Jose, CA 95138',
         copyright: '© 2024 Mind and Body Pain Clinic. All rights reserved.'
       },
       accessibility: {
         title: 'Accessibility',
         increaseText: 'Increase Text',
         decreaseText: 'Decrease Text',
         highContrast: 'High Contrast',
         reduceMotion: 'Reduce Motion',
         reset: 'Reset',
         close: 'Close'
       }
    },
    es: {
      nav: {
        home: 'Inicio',
        about: 'Sobre Nosotros',
        services: 'Servicios',
        traumaticBrainInjury: 'Lesión Cerebral Traumática',
        silverCreekSurgery: 'Manejo del Dolor',
        tms: 'TMS',
        contact: 'Contáctenos',
        patientPortal: 'Portal del Paciente',
        preOperativeInstructions: 'Instrucciones Preoperatorias',
        patientRightsAndPolicies: 'Derechos y Políticas del Paciente',
        workAndPersonalInjury: 'Trabajo & Lesiones Personales',
        blog: 'Blog',
        education: 'Educación',
        neuroanatomy: 'Neuroanatomía',
        innovativeIntegrativeMedicine: 'Medicina Innovadora e Integrativa'
      },
      hero: {
        welcome: 'Bienvenido a',
        subtitle: 'Soluciones integrales de manejo del dolor y bienestar.',
        getStarted: 'Comenzar',
        learnMore: 'Saber Más'
      },
      about: {
        title: 'Nuestra Misión',
        description: "En Mind and Body Pain Clinic, nos dedicamos a la evaluación integral y al manejo multimodal del dolor y las condiciones neurológicas. Nos especializamos en el tratamiento de dolores de cabeza, dolor de cuello y una amplia gama de otros trastornos dolorosos.\n\nNuestra misión es apoyar a los pacientes a través de la educación, el manejo de medicamentos y procedimientos intervencionistas avanzados. Creemos en la combinación de tecnologías médicas modernas con enfoques complementarios como la fisioterapia y el yoga para brindar una atención verdaderamente holística.\n\nTrabajamos en estrecha colaboración con sus proveedores de atención médica para garantizar un tratamiento individualizado y adaptado a sus necesidades. Si bien usamos medicamentos cuando es apropiado, generalmente evitamos los regímenes de opioides en dosis altas, ya que la investigación muestra que conllevan riesgos significativos con un beneficio limitado a largo plazo en comparación con estrategias más seguras y de dosis más bajas."
      },
             services: {
         title: 'Nuestros Servicios',
         traumaticBrainInjury: {
           title: 'Servicios de Lesiones Cerebrales Traumáticas',
           description: 'Planes de tratamiento holísticos para condiciones neurológicas.',
           learnMore: 'Saber más →',
           features: [
             'Planes de tratamiento personalizados',
             'Enfoque multidisciplinario',
             'Terapias basadas en evidencia'
           ]
         },
         silverCreekSurgery: {
           title: 'Servicios de Manejo del Dolor',
           description: 'Tratamientos de vanguardia para el dolor agudo y crónico.',
           learnMore: 'Saber más →',
           features: [
             'Procedimientos mínimamente invasivos',
             'Técnicas quirúrgicas avanzadas',
             'Atención postoperatoria integral'
           ]
         },
         brainTMS: {
           title: 'Servicios de TMS Cerebral',
           description: 'Terapia TMS para condiciones neurológicas y psiquiátricas.',
           learnMore: 'Saber más →',
           features: [
             'Tratamiento no invasivo',
             'Sin efectos secundarios de medicamentos',
             'Terapia aprobada por la FDA'
           ]
         }
       },
      contact: {
        title: 'Ponte en Contacto',
        subtitle: '¿Listo para comenzar tu viaje hacia el alivio del dolor? Contáctanos hoy para una consulta gratuita.',
        firstName: 'Nombre',
        lastName: 'Apellido',
        email: 'Correo Electrónico',
        message: 'Mensaje',
        sendMessage: 'Enviar Mensaje',
        sending: 'Enviando...'
      },
             footer: {
         description: 'Soluciones profesionales de manejo del dolor y bienestar.',
         contact: 'Contacto',
         address: '6010 Hellyer Ave Ste 150, San Jose, CA 95138',
         copyright: '© 2024 Mind and Body Pain Clinic. Todos los derechos reservados.'
       },
       accessibility: {
         title: 'Accesibilidad',
         increaseText: 'Aumentar Texto',
         decreaseText: 'Disminuir Texto',
         highContrast: 'Alto Contraste',
         reduceMotion: 'Reducir Movimiento',
         reset: 'Restablecer',
         close: 'Cerrar'
       }
    },
    hi: {
      nav: {
        home: 'होम',
        about: 'हमारे बारे में',
        services: 'सेवाएं',
        traumaticBrainInjury: 'दर्दनाक मस्तिष्क की चोट',
        silverCreekSurgery: 'दर्द प्रबंधन',
        tms: 'টিএমএস',
        contact: 'संपर्क करें',
        patientPortal: 'रोगी पोर्टल',
        preOperativeInstructions: 'प्री-ऑपरेटिव निर्देश',
        patientRightsAndPolicies: 'रोगी के अधिकार और नीतियां',
        workAndPersonalInjury: 'कार्य और व्यक्तिगत चोट',
        blog: 'ब्लॉग',
        education: 'शिक्षा',
        neuroanatomy: 'न्यूरोएनाटॉमी',
        innovativeIntegrativeMedicine: 'नवोन्मेषी और एकीकृत चिकित्सा'
      },
      hero: {
        welcome: 'स्वागत है',
        subtitle: 'व्यापक दर्द प्रबंधन और कल्याण समाधान।',
        getStarted: 'शुरू करें',
        learnMore: 'और जानें'
      },
      about: {
        title: 'हमारा मिशन',
        description: "माइंड एंड बॉडी पेन क्लिनिक में, हम दर्द और न्यूरोलॉजिकल स्थितियों के व्यापक मूल्यांकन और मल्टीमॉडल प्रबंधन के लिए समर्पित हैं। हम सिरदर्द, गर्दन के दर्द और कई अन्य दर्दनाक विकारों के इलाज में विशेषज्ञ हैं।\n\nहमारा मिशन शिक्षा, दवा प्रबंधन और उन्नत अंतःक्षेप प्रक्रियाओं के माध्यम से रोगियों का समर्थन करना है। हम वास्तव में समग्र देखभाल प्रदान करने के लिए आधुनिक चिकित्सा प्रौद्योगिकियों को फिजिकल थेरेपी और योग जैसे पूरक दृष्टिकोणों के साथ जोड़ने में विश्वास करते हैं।\n\nहम आपकी आवश्यकताओं के अनुरूप व्यक्तिगत उपचार सुनिश्चित करने के लिए आपके स्वास्थ्य सेवा प्रदाताओं के साथ मिलकर काम करते हैं। जबकि हम उचित होने पर दवाओं का उपयोग करते हैं, हम आम तौर पर उच्च-खुराक ओपिओइड आहार से बचते हैं, क्योंकि शोध से पता चलता है कि वे सुरक्षित, कम-खुराक रणनीतियों की तुलना में सीमित दीर्घकालिक लाभ के साथ महत्वपूर्ण जोखिम उठाते हैं।"
      },
      services: {
        traumaticBrainInjury: {
          title: 'दर्दनाक मस्तिष्क चोट सेवाएं',
          description: 'न्यूरोलॉजिकल स्थितियों के लिए समग्र उपचार योजनाएँ।',
          learnMore: 'और जानें →',
          features: [
            'व्यक्तिगत उपचार योजनाएं',
            'बहु-विषयक दृष्टिकोण',
            'साक्ष्य-आधारित उपचार'
          ]
        },
        interventionalPainManagement: {
          title: 'इंटरवेंशनल पेन मैनेजमेंट',
          description: 'पुराने दर्द का निदान और उपचार करने के लिए न्यूनतम इनवेंसिव प्रक्रियाएं।'
        },
        silverCreekSurgery: {
          title: 'दर्द प्रबंधन सेवाएं',
          description: 'तीव्र और पुराने दर्द के लिए अत्याधुनिक उपचार।',
        },
        brainTMS: {
          title: 'ब्रेन टीएमएस सेवाएं',
          description: 'न्यूरोलॉजिकल और मनोरोग स्थितियों के लिए टीएमएस थेरेपी।',
          learnMore: 'और जानें →',
          features: [
            'गैर-आक्रामक उपचार',
            'दवा के कोई दुष्प्रभाव नहीं',
            'एफडीए-अनुमोदित थेरेपी'
          ]
        }
      },
      contact: {
        title: 'संपर्क करें',
        subtitle: 'दर्द से राहत के अपने सफर को शुरू करने के लिए तैयार हैं? आज ही निःशुल्क परामर्श के लिए हमसे संपर्क करें।',
        firstName: 'पहला नाम',
        lastName: 'अंतिम नाम',
        email: 'ईमेल',
        message: 'संदेश',
        sendMessage: 'संदेश भेजें',
        sending: 'भेज रहे हैं...'
      },
      footer: {
        description: 'पेशेवर दर्द प्रबंधन और कल्याण समाधान।',
        contact: 'संपर्क',
        address: '6010 हेलियर एवेन्यू सुइट 150, सैन जोस, सीए 95138',
        copyright: '© 2024 माइंड एंड बॉडी पेन क्लिनिक। सर्वाधिकार सुरक्षित।'
      },
      accessibility: {
        title: 'सुलभता',
        increaseText: 'टेक्स्ट बढ़ाएं',
        decreaseText: 'टेक्स्ट कम करें',
        highContrast: 'उच्च कंट्रास्ट',
        reduceMotion: 'गति कम करें',
        reset: 'रीसेट करें',
                 close: 'बंद करें'
       }
    }
  };

  const t = content[language];

  const HomePage = () => {
    // Effect to seek video to 13 seconds after it loads
    React.useEffect(() => {
    }, []);

    return (
      <>
                           {/* Hero Section */}
        <section id="home" className="relative min-h-[100vh] flex items-center justify-center bg-white">
          <div className="w-full">
            <div className="relative bg-white overflow-hidden h-full">
              {/* Background Video */}
              <div className="absolute inset-0 z-0 h-full">
            {/* Vimeo Video Background */}
                <div className="absolute inset-0 w-full h-full overflow-hidden">
              <iframe
                    id="vimeo-player"
                    src="https://player.vimeo.com/video/242640045?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1&controls=0&playsinline=1&dnt=1&transparent=1#t=13s"
                className="w-full h-full object-cover"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Mind and Body Background Video"
                    loading="eager"
                    style={{ 
                      backgroundColor: 'transparent',
                      transform: 'scale(1.6)',
                      transformOrigin: 'center center'
                    }}
              ></iframe>
            </div>
                {/* Enhanced overlay for better text readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/30 to-black/40"></div>
          </div>
         
         {/* Content */}
               <div className="relative z-10 min-h-[100vh] flex items-center justify-center px-8">
           <div className="text-center">
                   <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-4 leading-tight tracking-tight drop-shadow-2xl">
                     Mind and Body Pain Clinic
               </h1>
                   <p className="text-xl sm:text-2xl text-white/90 leading-relaxed font-light drop-shadow-lg">
                 {t.hero.subtitle}
               </p>
                 </div>
               </div>
           </div>
         </div>
       </section>

      {/* Meet Dr. Singh Section */}
      <section id="meet-dr-singh" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 grid lg:grid-cols-2 gap-16 items-center">
          <div className="flex justify-center items-center">
            <img src="/harpreet-singh.jpg" alt="Dr. Harpreet Singh" className="w-full max-w-md h-auto object-cover"/>
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 tracking-tight">Meet Dr. Harpreet Singh</h2>
            <p className="text-md font-bold text-teal-600 mb-6">
              MD, Quadruple-Board Certified in Neurology, Internal Medicine, Interventional Pain Management, Brain Injury Medicine
            </p>

            {/* Medical Board Certifications */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-8 items-center justify-center md:justify-start">
                <img
                  src="/ABPMRlogo.png"
                  alt="ABPMR Board Certified"
                  className="h-20 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <img
                  src="/ABIMlogo.jpg"
                  alt="ABIM Board Certified"
                  className="h-20 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <img
                  src="/ABPNlogo.png"
                  alt="ABPN Board Certified"
                  className="h-20 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <img
                  src="/ABAlogo.webp"
                  alt="ABA Board Certified"
                  className="h-20 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>

            <p className="text-sm text-gray-700 leading-relaxed mb-4">
              Dr. Harpreet Singh is a highly specialized, quadruple board-certified physician with over 15 years of clinical experience. A recognized <strong>TBI (Traumatic Brain Injury) expert</strong>, he combines advanced medical technologies with holistic approaches to deliver compassionate, patient-centered treatment.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Dr. Singh's background spans primary care, palliative care, and academic medicine, including his role as Associate Clinical Professor at UC Davis Medical Center. Designated as a <strong>Qualified Medical Examiner (QME)</strong> by the California Division of Workers' Compensation, he also serves as an <strong>expert witness</strong>, evaluating disability and preparing medical-legal reports. With a focus on helping patients manage pain, recover from complex injuries, and restore quality of life, Dr. Singh integrates modern medical innovations with whole-person care.
            </p>
          </div>
        </div>
      </section>

      {/* About and Services Section */}
      <div className="relative bg-cover bg-center" style={{ backgroundImage: "url('/tealbackground.jpg')" }}>
        <div aria-hidden="true" className="absolute inset-0 bg-teal-800/70" />
        <section id="about-services" className="relative py-24">
          <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 grid lg:grid-cols-2 lg:gap-16">
            {/* Our Mission */}
            <div className="lg:pr-8">
              <h2 className="text-4xl sm:text-5xl font-extrabold mb-6 tracking-tight text-white">
                Our Mission
              </h2>
              <div className="w-20 h-1 bg-white/50 mb-6 rounded-full"></div>
              <p className="text-sm text-teal-100 leading-relaxed font-light">
                {t.about.description}
              </p>
            </div>
            {/* Our Services */}
            <div>
              <h2 className="text-4xl sm:text-5xl font-extrabold mb-6 tracking-tight text-white">
                Our Services
              </h2>
              <div className="w-20 h-1 bg-white/50 mb-6 rounded-full"></div>
              <div className="space-y-6">
                {/* Service Card 1 */}
                <Link to="/traumatic-brain-injury" className="block bg-teal-600/50 p-4 rounded-lg shadow-lg hover:bg-teal-600/70 transition-all duration-300">
                  <h3 className="text-xl font-bold mb-2 text-white">{t.services.traumaticBrainInjury.title}</h3>
                  <p className="text-sm text-teal-100 font-light mb-4">{t.services.traumaticBrainInjury.description}</p>
                  <span className="font-semibold text-white">
                    Learn more →
                  </span>
                </Link>
                {/* Service Card 2 */}
                <Link to="/pain-management" className="block bg-teal-600/50 p-4 rounded-lg shadow-lg hover:bg-teal-600/70 transition-all duration-300">
                  <h3 className="text-xl font-bold mb-2 text-white">Interventional Pain Management</h3>
                  <p className="text-sm text-teal-100 font-light mb-4">{t.services.silverCreekSurgery.description}</p>
                  <span className="font-semibold text-white">
                    Learn more →
                  </span>
                </Link>
                {/* Service Card 3 */}
                <Link to="/brain-tms" className="block bg-teal-600/50 p-4 rounded-lg shadow-lg hover:bg-teal-600/70 transition-all duration-300">
                  <h3 className="text-xl font-bold mb-2 text-white">{t.services.brainTMS.title}</h3>
                  <p className="text-sm text-teal-100 font-light mb-4">{t.services.brainTMS.description}</p>
                  <span className="font-semibold text-white">
                    Learn more →
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Understanding Your Health Section */}
      <section id="health-info" className="py-24 bg-gray-800">
        <div className="max-w-6xl mx-auto px-8 sm:px-12 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-8 tracking-tight">
              Understanding Your Health
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-teal-600 mx-auto mb-8 rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <Link to="/traumatic-brain-injury" className="block bg-gray-700 rounded-lg shadow-lg border border-gray-600 hover:shadow-xl hover:border-teal-500 transition-all duration-300 overflow-hidden">
              <img src="/brain.png" alt="Traumatic Brain Injury" className="w-full h-48 object-contain"/>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Traumatic Brain Injury</h3>
                <p className="text-sm text-gray-300 mb-6 font-light">Understand the causes, symptoms, and cutting-edge treatments for traumatic brain injuries.</p>
                <span className="font-semibold text-teal-400 hover:text-teal-300 transition-colors">Discover Treatments →</span>
              </div>
            </Link>
            {/* Card 2 */}
            <Link to="/neuroanatomy" className="block bg-gray-700 rounded-lg shadow-lg border border-gray-600 hover:shadow-xl hover:border-teal-500 transition-all duration-300 overflow-hidden">
              <img src="/neurodeg.png" alt="Neuroanatomy and Neurodegeneration" className="w-full h-48 object-contain"/>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Neuroanatomy and Neurodegeneration</h3>
                <p className="text-sm text-gray-300 mb-6 font-light">Explore brain structure, function, and progressive neurological conditions like Alzheimer's and Parkinson's.</p>
                <span className="font-semibold text-teal-400 hover:text-teal-300 transition-colors">Read More →</span>
              </div>
            </Link>
            {/* Card 3 */}
            <Link to="/pain-management" className="block bg-gray-700 rounded-lg shadow-lg border border-gray-600 hover:shadow-xl hover:border-teal-500 transition-all duration-300 overflow-hidden">
              <img src="/chronicpain.png" alt="Chronic Pain and Spine Disorders" className="w-full h-48 object-contain"/>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Chronic Pain and Spine Disorders</h3>
                <p className="text-sm text-gray-300 mb-6 font-light">Explore our advanced, multidisciplinary approach to treating chronic pain and complex spine-related conditions.</p>
                <span className="font-semibold text-teal-400 hover:text-teal-300 transition-colors">Improve Wellness →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-8 sm:px-12 lg:px-16">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-teal-600">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Medical Disclaimer</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              The information provided on this website is for informational and educational purposes only and is not intended as medical advice. It should not be used as a substitute for professional diagnosis or treatment. Always seek the advice of your physician or other qualified healthcare provider with any questions you may have regarding a medical condition or treatment. Do not disregard professional medical advice or delay seeking it because of something you have read on this website.
            </p>
          </div>
        </div>
      </section>
      </>
   );
   };

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<AppLayout
            t={t}
            language={language}
            handleLanguageChange={handleLanguageChange}
            showLanguageDropdown={showLanguageDropdown}
            setShowLanguageDropdown={setShowLanguageDropdown}
            accessibilitySettings={accessibilitySettings}
            toggleAccessibilityPanel={toggleAccessibilityPanel}
            handleAccessibilityChange={handleAccessibilityChange}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            servicesOpen={servicesOpen}
            setServicesOpen={setServicesOpen}
            patientPortalOpen={patientPortalOpen}
            setPatientPortalOpen={setPatientPortalOpen}
            educationOpen={educationOpen}
            setEducationOpen={setEducationOpen}
          />}>
          <Route index element={<HomePage />} />
          <Route path="traumatic-brain-injury" element={<TraumaticBrainInjury />} />
          <Route path="long-term-effects-tbi" element={<LongTermEffectsTBI />} />
          <Route path="pain-management" element={<PainManagement />} />
          <Route path="brain-tms" element={<BrainTMS />} />
          <Route path="additional-treatments" element={<AdditionalTreatments />} />
          <Route path="work-and-personal-injury" element={<WorkAndPersonalInjury />} />
          <Route path="contact" element={<Contact />} />
          <Route path="pre-operative-instructions" element={<PreOperativeInstructions />} />
          <Route path="patient-rights-and-policies" element={<PatientRightsAndPolicies />} />
          <Route path="blog" element={<Blog />} />
          <Route path="job-openings" element={<JobOpenings />} />
          <Route path="all-treatments" element={<AllTreatments />} />
          <Route path="neuroanatomy" element={<Neuroanatomy />} />
          <Route path="innovative-integrative-medicine" element={<InnovativeIntegrativeMedicine />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;



